import React, { useState } from 'react';
import {
  Box, Chip, Collapse, Divider, IconButton, Paper, Stack,
  Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Code as CodeIcon,
  TableChart as DataIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';

/**
 * Structured problem/task presentation card.
 * Replaces the plain "Your task" text dump.
 */
const ProblemCard = ({ item, accentColor, defaultExpanded = true }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const context = item?.context || {};
  const taskType = context.task_type || 'generic';
  const taskDescription = context.task_description;
  const targetQuestion = context.target_question;
  const sampleData = context.sample_data;
  const schema = context.schema;
  const constraints = context.constraints; // optional array of strings
  const expectedOutput = context.expected_output; // optional string

  const color = accentColor || theme.palette.primary.main;

  // Split the prompt into paragraphs for readability
  const paragraphs = (item?.prompt || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const LANG_LABELS = {
    python: 'Python',
    'data-analysis': 'Python / pandas',
    sql: 'SQL',
    excel: 'Excel',
    generic: 'Open',
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: alpha(color, 0.4),
        bgcolor: alpha(color, 0.03),
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2, py: 1.25,
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: alpha(color, 0.2),
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { bgcolor: alpha(color, 0.04) },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TaskIcon sx={{ fontSize: 16, color }} />
          <Typography variant="caption" fontWeight={800} sx={{ color, textTransform: 'uppercase', letterSpacing: 1 }}>
            Scenario
          </Typography>
          <Chip
            label={LANG_LABELS[taskType] || taskType}
            size="small"
            sx={{
              height: 18, fontSize: '0.7rem', fontWeight: 700,
              bgcolor: alpha(color, 0.1), color, border: 'none',
            }}
          />
        </Stack>
        <Tooltip title={expanded ? 'Collapse task' : 'Expand task'}>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {/* Main prompt */}
          <Stack spacing={0.75}>
            {paragraphs.map((para, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ lineHeight: 1.7, fontWeight: i === 0 ? 600 : 400 }}
              >
                {para}
              </Typography>
            ))}
          </Stack>

          {targetQuestion && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                Target question
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {targetQuestion}
              </Typography>
            </>
          )}

          {/* Constraints */}
          {constraints?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.75}>
                Constraints
              </Typography>
              <Stack spacing={0.5}>
                {constraints.map((c, i) => (
                  <Typography key={i} variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.75 }}>
                    <span style={{ color, flexShrink: 0 }}>›</span>
                    {c}
                  </Typography>
                ))}
              </Stack>
            </>
          )}

          {/* Expected output */}
          {expectedOutput && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                Expected output
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{expectedOutput}</Typography>
            </>
          )}

          {/* Schema chips */}
          {schema && Object.keys(schema).length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75} flexWrap="wrap">
                <DataIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Data schema
                </Typography>
              </Stack>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Object.entries(schema).map(([col, dtype]) => (
                  <Chip
                    key={col}
                    label={`${col}: ${dtype}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
                  />
                ))}
              </Box>
            </>
          )}

          {/* Sample data */}
          {sampleData && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75}>
                <CodeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Sample data
                </Typography>
              </Stack>
              <Box
                component="pre"
                sx={{
                  m: 0, p: 1.5,
                  bgcolor: '#0d0d0d', color: '#abb2bf',
                  fontFamily: 'monospace', fontSize: '0.78rem',
                  borderRadius: 1, overflow: 'auto', maxHeight: 180,
                  border: '1px solid #333',
                }}
              >
                {typeof sampleData === 'string' ? sampleData : JSON.stringify(sampleData, null, 2)}
              </Box>
            </>
          )}

          {/* Deliverable / task description (if different from prompt) */}
          {taskDescription && taskDescription !== item?.prompt && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                Deliverable
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {taskDescription}
              </Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ProblemCard;
